"use client"

import { Form, Formik } from "formik"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { FormChannelSelect } from "@/components/form-inputs"
import { DestinationList } from "./destination-list"
import { useAccountAlertChannelsQuery } from "@/hooks/queries/alert-channels"
import { useUpdateAccountAlertChannelsMutation } from "@/hooks/mutations/alert-channels"
import { useNotificationChannelOptions } from "@/hooks/notifications/use-channel-options"
import { buildDestinations, toDestinationCandidates } from "./destinations"
import type { NotificationChannelView } from "@/hooks/notifications/use-notification-channels"
import { useTranslations } from "next-intl"


interface AccountAlertsCardProps {
  /** Already loaded by the page — the addresses live in `config`, which the options list omits. */
  channels: NotificationChannelView[]
}

/**
 * Routing for alerts about the workspace itself, as opposed to a single conversation.
 * These cannot be turned off — each one reports that work has already stopped.
 */
export function AccountAlertsCard({ channels }: AccountAlertsCardProps) {
  const tUi = useTranslations("NotificationChannels.NotificationChannelsAccountAlertsCard")

  const { data, isLoading } = useAccountAlertChannelsQuery()
  const update = useUpdateAccountAlertChannelsMutation()
  // `deliverable` is decided server-side; the client cannot recompute it, since the
  // encrypted config keys never reach the browser.
  const { options, loading } = useNotificationChannelOptions()

  // Saved state, not the pending selection: this is where alerts go right now.
  const { destinations, systemChannelMissing } = buildDestinations(
    toDestinationCandidates(channels, options),
    data?.channelIds ?? [],
  )

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base"><span key={tUi("accountAlerts")}>{tUi("accountAlerts")}</span></CardTitle>
        <p className="text-sm text-muted-foreground">
          <span key={tUi("sentWhenTheWholeWorkspaceNeedsAttentionThe")}>{tUi("sentWhenTheWholeWorkspaceNeedsAttentionThe")}</span></p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Formik
          enableReinitialize
          initialValues={{ channelIds: data?.channelIds ?? [] }}
          // Must return a promise: on a sync handler Formik leaves `isSubmitting` latched,
          // and a failed save would disable Save until the card remounts.
          onSubmit={(values) => update.mutateAsync(values).catch(() => {})}
        >
          {({ dirty, isSubmitting }) => (
            <Form className="flex flex-wrap items-center gap-2">
              <FormChannelSelect
                name="channelIds"
                options={options}
                emptyLabel="Versafy (default)"
                disabled={isLoading || update.isPending}
                loading={loading}
              />
              <Button type="submit" size="sm" disabled={isLoading || update.isPending || isSubmitting || !dirty}>
                <span key={tUi("save")}>{tUi("save")}</span></Button>
            </Form>
          )}
        </Formik>

        <DestinationList
          destinations={destinations}
          emptyFallbackNote={
            systemChannelMissing
              ? data?.fallbackEmail
                ? tUi("fallbackOwner", {email: data.fallbackEmail})
                : tUi("nowhere")
              : undefined
          }
        />
      </CardContent>
    </Card>
  )
}
